import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { categories } from '@/mocks/homeData';

export default function Navbar() {
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [mobileDropdownOpen, setMobileDropdownOpen] = useState(false);

  // Lock body scroll when mobile menu open
  useEffect(() => {
    if (mobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [mobileMenuOpen]);

  const closeMobileMenu = () => {
    setMobileMenuOpen(false);
    setMobileDropdownOpen(false);
  };

  return (
    <>
      <nav className="fixed top-0 left-0 right-0 z-50 bg-background-50/90 backdrop-blur-md border-b border-background-200/60">
        <div className="flex items-center justify-between px-6 md:px-10 h-16">
          {/* Left: Logo + Desktop Nav */}
          <div className="flex items-center gap-8">
            <Link to="/" className="flex items-center gap-3 group shrink-0">
              <div className="w-9 h-9 rounded-full bg-primary-500 flex items-center justify-center">
                <i className="ri-shopping-bag-3-line text-background-50 text-sm"></i>
              </div>
              <span className="font-heading text-lg font-bold text-foreground-900 whitespace-nowrap hidden sm:block">
                OOPBuy Spreadsheet
              </span>
            </Link>

            {/* Desktop Nav Links */}
            <div className="hidden lg:flex items-center gap-6 text-sm">
              <Link to="/" className="text-foreground-700 hover:text-foreground-950 whitespace-nowrap transition-colors font-medium">Startseite</Link>
              <Link to="/artikel/oopbuy-spreadsheet-guide" className="text-foreground-700 hover:text-foreground-950 whitespace-nowrap transition-colors">Einkaufsführer</Link>
              <div
                className="relative"
                onMouseEnter={() => setDropdownOpen(true)}
                onMouseLeave={() => setDropdownOpen(false)}
              >
                <button className="flex items-center gap-1 text-foreground-700 hover:text-foreground-950 whitespace-nowrap transition-colors cursor-pointer">
                  Kategorien
                  <i className={`ri-arrow-down-s-line text-xs transition-transform duration-200 ${dropdownOpen ? 'rotate-180' : ''}`}></i>
                </button>
                {dropdownOpen && (
                  <div className="absolute top-full left-0 mt-2 w-60 bg-background-50 rounded-xl border border-background-200/70 shadow-lg py-2 z-50 max-h-[400px] overflow-y-auto">
                    {categories.map((cat) => (
                      <a
                        key={cat.name}
                        href={cat.url}
                        target="_blank"
                        rel="noopener nofollow"
                        className="block px-4 py-2.5 text-sm text-foreground-700 hover:bg-background-100 hover:text-foreground-950 transition-colors whitespace-nowrap"
                      >
                        {cat.name}
                      </a>
                    ))}
                  </div>
                )}
              </div>
              <a href="#vorteile" className="text-foreground-700 hover:text-foreground-950 whitespace-nowrap transition-colors">Vorteile</a>
              <a href="#faq" className="text-foreground-700 hover:text-foreground-950 whitespace-nowrap transition-colors">FAQ</a>
            </div>
          </div>

          {/* Right: CTA Buttons + Hamburger */}
          <div className="flex items-center gap-3">
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="hidden sm:inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-foreground-300 text-sm font-medium text-foreground-900 hover:bg-foreground-50 transition-colors whitespace-nowrap cursor-pointer"
            >
              oopbuy spreadsheet
              <i className="ri-arrow-right-up-line text-xs"></i>
            </a>
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="hidden sm:inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-foreground-900 text-background-50 text-sm font-medium hover:bg-foreground-800 transition-colors whitespace-nowrap cursor-pointer"
            >
              buy it now
              <i className="ri-arrow-right-up-line text-xs"></i>
            </a>

            {/* Hamburger Button */}
            <button
              onClick={() => setMobileMenuOpen((prev) => !prev)}
              className="lg:hidden flex items-center justify-center w-10 h-10 rounded-lg hover:bg-background-100 transition-colors cursor-pointer"
              aria-label={mobileMenuOpen ? 'Menü schließen' : 'Menü öffnen'}
              aria-expanded={mobileMenuOpen}
            >
              {mobileMenuOpen ? (
                <i className="ri-close-line text-xl text-foreground-900"></i>
              ) : (
                <i className="ri-menu-line text-xl text-foreground-900"></i>
              )}
            </button>
          </div>
        </div>
      </nav>

      {/* Mobile Menu Overlay + Panel — rendered OUTSIDE <nav> so backdrop-filter does not clip fixed positioning */}
      {mobileMenuOpen && (
        <>
          <div
            className="fixed inset-0 bg-black/40 z-[60] lg:hidden"
            onClick={closeMobileMenu}
            aria-hidden="true"
          ></div>

          <div className="fixed top-16 right-0 bottom-0 w-[300px] max-w-[85vw] bg-background-50 border-l border-background-200/60 z-[70] lg:hidden overflow-y-auto shadow-2xl">
            <div className="p-5 space-y-1">
              {/* Mobile Nav Links */}
              <Link
                to="/"
                onClick={closeMobileMenu}
                className="flex items-center gap-3 px-4 py-3 rounded-lg text-foreground-900 font-medium hover:bg-background-100 transition-colors"
              >
                <i className="ri-home-4-line text-lg w-5 flex items-center justify-center"></i>
                Startseite
              </Link>

              <Link
                to="/artikel/oopbuy-spreadsheet-guide"
                onClick={closeMobileMenu}
                className="flex items-center gap-3 px-4 py-3 rounded-lg text-foreground-900 font-medium hover:bg-background-100 transition-colors"
              >
                <i className="ri-book-open-line text-lg w-5 flex items-center justify-center"></i>
                Einkaufsführer
              </Link>

              {/* Mobile Categories Dropdown */}
              <div>
                <button
                  onClick={() => setMobileDropdownOpen((prev) => !prev)}
                  className="flex items-center justify-between w-full px-4 py-3 rounded-lg text-foreground-900 font-medium hover:bg-background-100 transition-colors cursor-pointer"
                >
                  <span className="flex items-center gap-3">
                    <i className="ri-grid-line text-lg w-5 flex items-center justify-center"></i>
                    Kategorien
                  </span>
                  <i className={`ri-arrow-down-s-line text-sm transition-transform duration-200 ${mobileDropdownOpen ? 'rotate-180' : ''}`}></i>
                </button>
                {mobileDropdownOpen && (
                  <div className="ml-6 mt-1 space-y-1 border-l-2 border-background-200/60 pl-4">
                    {categories.map((cat) => (
                      <a
                        key={cat.name}
                        href={cat.url}
                        target="_blank"
                        rel="noopener nofollow"
                        onClick={closeMobileMenu}
                        className="block px-3 py-2.5 text-sm text-foreground-700 hover:text-foreground-950 hover:bg-background-100 rounded-lg transition-colors whitespace-nowrap"
                      >
                        {cat.name}
                      </a>
                    ))}
                  </div>
                )}
              </div>

              <a
                href="#vorteile"
                onClick={closeMobileMenu}
                className="flex items-center gap-3 px-4 py-3 rounded-lg text-foreground-900 font-medium hover:bg-background-100 transition-colors"
              >
                <i className="ri-star-line text-lg w-5 flex items-center justify-center"></i>
                Vorteile
              </a>

              <a
                href="#faq"
                onClick={closeMobileMenu}
                className="flex items-center gap-3 px-4 py-3 rounded-lg text-foreground-900 font-medium hover:bg-background-100 transition-colors"
              >
                <i className="ri-question-line text-lg w-5 flex items-center justify-center"></i>
                FAQ
              </a>

              <div className="border-t border-background-200/60 my-3 pt-3 space-y-2">
                <a
                  href="https://kaspreadsheet.com"
                  target="_blank"
                  rel="noopener nofollow"
                  onClick={closeMobileMenu}
                  className="flex items-center justify-center gap-2 w-full px-4 py-3 rounded-lg border border-foreground-300 text-sm font-medium text-foreground-900 hover:bg-foreground-50 transition-colors whitespace-nowrap cursor-pointer"
                >
                  oopbuy spreadsheet
                  <i className="ri-arrow-right-up-line text-xs"></i>
                </a>
                <a
                  href="https://kaspreadsheet.com"
                  target="_blank"
                  rel="noopener nofollow"
                  onClick={closeMobileMenu}
                  className="flex items-center justify-center gap-2 w-full px-4 py-3 rounded-lg bg-foreground-900 text-background-50 text-sm font-medium hover:bg-foreground-800 transition-colors whitespace-nowrap cursor-pointer"
                >
                  buy it now
                  <i className="ri-arrow-right-up-line text-xs"></i>
                </a>
              </div>
            </div>
          </div>
        </>
      )}
    </>
  );
}